/* eslint-disable react/prop-types */
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CheckOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';

/**
 * Light custom select — Lessons Learned SingleSelect sister.
 * Avoids native/OS dark menus and global antd select skins.
 */
const AdminAiSelect = ({
  label,
  options = [],
  value = '',
  onChange,
  placeholder = 'Select…',
  disabled = false,
  searchable = true,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();

  const normalized = useMemo(
    () =>
      options.map((o) =>
        typeof o === 'string'
          ? { value: o, label: o }
          : { value: String(o.value), label: String(o.label ?? o.value) }
      ),
    [options]
  );

  const selected = normalized.find((o) => o.value === String(value));
  const display = selected?.label || '';

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [normalized, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open && searchable) {
      const t = window.setTimeout(() => searchRef.current?.focus(), 40);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, searchable]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setQuery('');
    }
  }, [disabled]);

  const pick = (next) => {
    onChange?.(next);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={`admin-ai-select-wrap ${className}`.trim()} ref={rootRef}>
      {label ? <span className="admin-ai-label">{label}</span> : null}
      <button
        type="button"
        className={`admin-ai-select-trigger${open ? ' is-open' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span className={`admin-ai-select-value${display ? '' : ' is-placeholder'}`}>
          {display || placeholder}
        </span>
        <DownOutlined className="admin-ai-select-caret" />
      </button>

      {open ? (
        <div className="admin-ai-select-menu" role="listbox" id={listId}>
          {searchable && normalized.length > 6 ? (
            <div className="admin-ai-select-search">
              <SearchOutlined />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                aria-label="Search options"
              />
            </div>
          ) : null}
          <div className="admin-ai-select-list">
            {filtered.length === 0 ? (
              <div className="admin-ai-select-empty">No matches</div>
            ) : (
              filtered.map((o) => {
                const active = o.value === String(value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`admin-ai-select-option${active ? ' is-active' : ''}`}
                    onClick={() => pick(o.value)}
                    title={o.label}
                  >
                    <span>{o.label}</span>
                    {active ? <CheckOutlined /> : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminAiSelect;
